import React, { useCallback, useEffect, useState } from "react";
import type {
	IPhotoLibraryPhoto,
	IPhotoLibrarySection,
	IUploadingFile,
} from "../../../interfaces/photoLibrary";
import { useToast } from "../../../context/ToasterContext";
import { MAX_FILE_SIZE_FOR_PHOTO_LIBRARY, MAX_SELECTABLE_PHOTOS } from "../../../constants/commonConstants";
import { useAppDispatch, useAppSelector } from "../../../hooks/storeHooks";
import {
	addNewSectionThunk,
	addPhotoToSectionThunk,
	addPhotoToSystemSectionThunk,
	deletePhotoFromGalleryThunk,
	deleteSectionThunk,
	getPhotoLibraryPhotosThunk,
	getPhotoLibrarySectionsThunk,
	unlinkPhotoFromSectionThunk,
	updateSectionNameThunk,
	uploadPhotoToS3Thunk,
} from "../../../store/b2b/photoLibrary/photoLibraryThunk";
import {
	DefaultVendorPhotoLibrarySectionIdsEnum,
	DefaultVendorPhotoLibrarySectionNamesEnum,
	VendorPhotoLibraryTypesEnum,
} from "../../../constants/enums";
import { setActiveSection, setLibraryPhotos } from "../../../store/b2b/photoLibrary/photoLibrarySlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { REDUX_REQUEST_STATUS } from "../../../types/commonTypes";
import { PHOTO_LIBRARY_PHOTOS_PATH } from "../../../constants/formConstants";
import PhotoLibraryConfirmationboxes from "./PhotoLibraryConfirmationboxes";
import { checkFileTypeForPhotoLibrary, compressImages } from "../../../utils/commonUtils";
import { IAttachmentFile } from "../../../interfaces/corporateEvent";
import PhotoLibraryHeader from "../../reusable-components/b2b/photo-library-components/PhotoLibraryHeader";
import PhotoLibrarySidebar from "../../reusable-components/b2b/photo-library-components/PhotoLibrarySidebar";
import PhotoLibraryUploadZone from "../../reusable-components/b2b/photo-library-components/PhotoLibraryUploadZone";
import PhotoLibraryUploadProgress from "../../reusable-components/b2b/photo-library-components/PhotoLibraryUploadProgress";
import PhotoLibraryGrid from "../../reusable-components/b2b/photo-library-components/PhotoLibraryGrid";
import PhotoLibraryFooter from "../../reusable-components/b2b/photo-library-components/PhotoLibraryFooter";
import { getFileNameFromUrl } from "../../../helpers/common";
import "./photoLibrary.scss";

interface IPreviewPhotoProps {
	selectedAttachments: IAttachmentFile[];
	onClose: () => void;
	triggerPreviewPhoto: (photo: IPhotoLibraryPhoto | null) => void;
	onAttachmentsChange: (attachments: IAttachmentFile[]) => void;
}

const PhotoLibrary = ({
	onClose,
	triggerPreviewPhoto,
	onAttachmentsChange,
	selectedAttachments,
}: IPreviewPhotoProps) => {
	const dispatch = useAppDispatch();
	const { photoLibrarySections, libraryPhotos, activeSection, isLoading, isLoadingPhotos } = useAppSelector(
		(state) => state.photoLibrary,
	);
	const [selectedPhotos, setSelectedPhotos] = useState<IPhotoLibraryPhoto[]>([]);
	const [uploadingFiles, setUploadingFiles] = useState<IUploadingFile[]>([]);
	const [isDragging, setIsDragging] = useState(false);
	const [hoveredPhotoId, setHoveredPhotoId] = useState<string | null>(null);
	const [draggedPhotoId, setDraggedPhotoId] = useState<string | null>(null);
	const [dropTargetSection, setDropTargetSection] = useState<string | null>(null);
	const [dragOverPhotoId, setDragOverPhotoId] = useState<string | null>(null);
	const [deleteConfirmPhotoId, setDeleteConfirmPhotoId] = useState<string | null>(null);
	const [unlinkConfirmPhotoId, setUnlinkConfirmPhotoId] = useState<string | null>(null);
	const [showAddSectionModal, setShowAddSectionModal] = useState(false);
	const [renameSectionData, setRenameSectionData] = useState<IPhotoLibrarySection | null>(null);
	const [deleteSectionData, setDeleteSectionData] = useState<IPhotoLibrarySection | null>(null);
	const { showToast } = useToast();
	const { userDetails } = useAppSelector((state) => state.user);
	const allPhotosSectionName = DefaultVendorPhotoLibrarySectionNamesEnum.AllPhotos;
	const vendorProfileSectionName = DefaultVendorPhotoLibrarySectionNamesEnum.VendorProfileImages;
	const allPhotosSectionId = DefaultVendorPhotoLibrarySectionIdsEnum.AllPhotos;
	const uncategorizedSectionId = DefaultVendorPhotoLibrarySectionIdsEnum.Uncategorized;
	const vendorProfileSectionId = DefaultVendorPhotoLibrarySectionIdsEnum.VendorProfileImages;

	useEffect(() => {
		if (selectedAttachments?.length > 0) {
			const selectedPhotoLibraryPhotos: IPhotoLibraryPhoto[] = selectedAttachments.map(
				(attachment, index) =>
					libraryPhotos?.find((photo) => photo.id === attachment.id) ?? {
						id: attachment.id ?? "",
						fileName: attachment.fileName?.trim()
							? attachment.fileName
							: getFileNameFromUrl(attachment?.fileUrl, index),
						fileUrl: attachment.fileUrl ?? "",
						createdAt: attachment.createdAt ?? "",
						updatedAt: attachment.updatedAt ?? "",
						vendorId: userDetails?.vendorId ?? "",
						sectionId: undefined,
						isProfileImage: false,
					},
			);

			setSelectedPhotos(selectedPhotoLibraryPhotos);
		}
		setFirstSectionAsActive();
		dispatch(getPhotoLibraryPhotosThunk({ sectionId: `${VendorPhotoLibraryTypesEnum.All}` }));
	}, []);

	const getTransformedSections = () => {
		dispatch(getPhotoLibrarySectionsThunk());
	};

	const handleSectionDragOver = (section: string, e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (section === allPhotosSectionName || section === vendorProfileSectionName) {
			setDropTargetSection(null);
			return;
		}

		setDropTargetSection(section);
	};

	const handleSectionDragLeave = () => {
		setDropTargetSection(null);
	};

	// Internal function that performs API call without state update
	const _addPhotoToSectionAPIInternal = async (
		sectionId: string,
		fileName: string,
		fileUrl: string,
		id?: string,
		onProgress?: (progress: number) => void,
	) => {
		const response = await dispatch(
			addPhotoToSectionThunk({ sectionId, files: [{ fileName, fileUrl, id }], onProgress }),
		);
		if (response.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			const unwrappedResponse = unwrapResult(response);
			return {
				status: true,
				photos: unwrappedResponse,
			};
		} else {
			return {
				status: false,
				photos: [],
			};
		}
	};

	// Public function that updates state immediately (for single operations)
	const triggerAddPhotoToSectionAPI = async (
		sectionId: string,
		fileName: string,
		fileUrl: string,
		id?: string,
		onProgress?: (progress: number) => void,
	) => {
		const result = await _addPhotoToSectionAPIInternal(sectionId, fileName, fileUrl, id, onProgress);

		if (result.status && result.photos?.length) {
			if (activeSection?.id !== allPhotosSectionId) {
				const addedPhotoIds = new Set(result.photos.map((p) => p.id));
				const updatedLibraryPhotos = libraryPhotos?.filter((p) => !addedPhotoIds.has(p.id));
				dispatch(setLibraryPhotos(updatedLibraryPhotos ?? []));
			}
			getTransformedSections();
		}

		return result;
	};

	const movePhotoToSection = async (file: IPhotoLibraryPhoto, section: IPhotoLibrarySection) => {
		if (section.id === allPhotosSectionId) {
			showToast("Not allowed to drop (All Photos is a virtual view).", undefined, "error");
			return;
		} else {
			if (!file) {
				showToast("Invalid photo.");
				return;
			}

			if (section.id === file?.sectionId) {
				showToast("Photo is already part of this section.", undefined, "error");
				setDropTargetSection(null);
				return;
			}

			if (!file?.sectionId && section.id === DefaultVendorPhotoLibrarySectionIdsEnum.Uncategorized) {
				showToast("Photo is already part of this section.", undefined, "info");
				setDropTargetSection(null);
				return;
			}

			if (section.isSystemSection) {
				const isSuccess = await triggerUnlinkPhotoFromSectionAPI(file);
				if (isSuccess.status) {
					showToast(`Photo moved to Uncategorized section.`, undefined, "success");
				}
			} else {
				// used for unlinking photo from section
				const isSuccess = await triggerAddPhotoToSectionAPI(section.id, file.fileName, file.fileUrl, file.id);
				if (isSuccess.status) {
					showToast(`Photo moved to '${section.sectionName}'.`, undefined, "success");
				}
			}
		}
	};

	const addNewSection = async () => {
		setShowAddSectionModal(true);
	};

	const handleAddSection = async (sectionName: string) => {
		const response = await dispatch(addNewSectionThunk({ sectionName }));
		if (response.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			showToast(`Section '${sectionName}' created`, undefined, "success");
			getTransformedSections();
		}
	};

	const triggerRenameSectionAPI = async (sectionId: string, newName: string) => {
		const response = await dispatch(updateSectionNameThunk({ sectionId, sectionName: newName }));
		if (response.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			showToast(`Section renamed to '${newName}' successfully`, undefined, "success");
			getTransformedSections();
			return true;
		} else {
			return false;
		}
	};

	const renameSection = async (section: IPhotoLibrarySection) => {
		setRenameSectionData(section);
	};

	const deleteSection = async (section: IPhotoLibrarySection) => {
		setDeleteSectionData(section);
	};

	const handleDeleteSection = async (section: IPhotoLibrarySection) => {
		const deleteSectionResponse = await dispatch(deleteSectionThunk({ sectionId: section.id }));
		if (deleteSectionResponse.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			const toastMessage =
				`Section '${section.sectionName}' deleted` +
				(section.photoCount > 0 ? ` ${section.photoCount} photos moved to Uncategorized.` : "");
			showToast(toastMessage, undefined, "success");
			getTransformedSections();
		} else {
			showToast(
				deleteSectionResponse.payload?.message ?? "Failed to delete section. Please try again.",
				undefined,
				"error",
			);
		}

		if (activeSection?.id === section.id) {
			dispatch(getPhotoLibraryPhotosThunk({ sectionId: `${VendorPhotoLibraryTypesEnum.All}` }));
		}
	};

	const setFirstSectionAsActive = () => {
		dispatch(getPhotoLibrarySectionsThunk()).then((response) => {
			if (response.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
				const unwrappedResponse = unwrapResult(response);
				dispatch(setActiveSection(unwrappedResponse[0] ?? null));
			}
		});
	};

	const getFilteredPhotos = () => {
		if (activeSection?.id === allPhotosSectionId || activeSection?.id === vendorProfileSectionId) {
			return libraryPhotos ?? [];
		} else if (activeSection?.id === uncategorizedSectionId) {
			return libraryPhotos?.filter((p) => !p.sectionId) ?? [];
		} else if (activeSection?.id === vendorProfileSectionId) {
			return libraryPhotos?.filter((p) => p.isProfileImage) ?? [];
		}
		return libraryPhotos?.filter((p) => p.sectionId === activeSection?.id) ?? [];
	};

	const handleSectionDrop = (section: IPhotoLibrarySection, e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const draggedPhoto = libraryPhotos?.find((p) => p.id === draggedPhotoId);

		if (
			section.isSystemSection &&
			section.sectionName !== DefaultVendorPhotoLibrarySectionNamesEnum.Uncategorized
		) {
			if (section.sectionName === vendorProfileSectionName) {
				showToast("Drag & drop is disabled for Vendor Profile Images.", undefined, "error");
			} else {
				showToast("Not allowed to drop (All Photos is a virtual view).", undefined, "error");
			}
			setDraggedPhotoId(null);
			setDropTargetSection(null);
			return;
		}

		if (draggedPhoto) {
			movePhotoToSection(draggedPhoto, section);
		}

		setDraggedPhotoId(null);
		setDropTargetSection(null);
	};

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	// Drag & Drop for photos
	const handlePhotoDragStart = (photo: IPhotoLibraryPhoto, e: React.DragEvent) => {
		e.stopPropagation();
		if (activeSection?.sectionName === vendorProfileSectionName || photo?.isProfileImage) {
			showToast("Drag & drop is disabled for Vendor Profile Images.", undefined, "error");
			return;
		}
		setDraggedPhotoId(photo.id);
		e.dataTransfer.effectAllowed = "move";
	};

	const handlePhotoDragEnd = () => {
		setDraggedPhotoId(null);
		setDropTargetSection(null);
		setDragOverPhotoId(null);
	};

	// Internal function that performs API call without state update
	const _addPhotoToSystemSectionAPIInternal = async (
		fileName: string,
		fileUrl: string,
		id?: string,
		onProgressCallback?: (progress: number) => void,
	) => {
		const response = await dispatch(
			addPhotoToSystemSectionThunk({ files: [{ fileName, fileUrl, id }], onProgress: onProgressCallback }),
		);
		if (response.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			const unwrappedResponse = unwrapResult(response);
			return {
				status: true,
				photos: unwrappedResponse,
			};
		} else {
			return {
				status: false,
				photos: null,
			};
		}
	};

	const setProgress = (uploadId: string, progress: number) => {
		setUploadingFiles((prev) =>
			prev.map((file) => (file.id === uploadId ? { ...file, progress: progress } : file)),
		);
	};

	const simulateUpload = async (upload: IUploadingFile) => {
		const uploadId = upload.id;
		const fileToUpload = upload.file;

		try {
			const vendorName = `${userDetails?.firstName} ${userDetails?.lastName}`;
			const directoryPath = PHOTO_LIBRARY_PHOTOS_PATH.replace(":vendorName", vendorName);

			const fileResponse = await dispatch(
				uploadPhotoToS3Thunk({
					file: (await compressImages(fileToUpload, (percent) => {
						setProgress(uploadId, Math.min(percent / 2, 33));
					})) as File,
					directory: directoryPath,
					onProgress: (percent) => {
						setProgress(uploadId, Math.min(33 + percent / 2));
					},
				}),
			);

			const unwrappedResponse = unwrapResult(fileResponse);

			if (fileResponse.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED && unwrappedResponse?.imageUrl) {
				const sectionId = activeSection?.id;
				// Use internal functions that don't update state immediately
				const isSuccess =
					sectionId === allPhotosSectionId || sectionId === uncategorizedSectionId
						? await _addPhotoToSystemSectionAPIInternal(
								fileToUpload.name,
								unwrappedResponse.imageUrl,
								"",
								(percent) => {
									setProgress(uploadId, 66 + percent / 2);
								},
							)
						: await _addPhotoToSectionAPIInternal(
								sectionId ?? "",
								fileToUpload.name,
								unwrappedResponse.imageUrl,
								"",
								(percent) => {
									setProgress(uploadId, 66 + percent / 2);
								},
							);

				if (isSuccess.status) {
					setProgress(uploadId, 100);
					// Return the result so we can batch update state
					return isSuccess;
				} else {
					showToast("Failed to add photo to section. Please try again.", undefined, "error");
					return null;
				}
			} else {
				throw new Error("Upload failed");
			}
		} catch (error) {
			showToast("Failed to upload photo. Please try again.", undefined, "error");
			setUploadingFiles((prev) =>
				prev.map((file) => (file.id === upload.id ? { ...file, status: "error" as const } : file)),
			);
			return null;
		}
	};

	const handleFileSelect = useCallback(
		async (files: FileList | null) => {
			if (!files) return;

			const filesArray = Array.from(files);
			let hasInvalidSize = false;
			let hasInvalidType = false;

			const validFiles = filesArray.filter((file) => {
				const isValidType = checkFileTypeForPhotoLibrary(file);
				const isValidSize = file.size <= MAX_FILE_SIZE_FOR_PHOTO_LIBRARY;

				if (!isValidSize) hasInvalidSize = true;
				if (!isValidType) hasInvalidType = true;

				return isValidType && isValidSize;
			});

			if (hasInvalidSize) showToast("File size should be less than 10MB", undefined, "error");
			if (hasInvalidType)
				showToast("Invalid file type. Only PNG, JPEG and JPG files are allowed", undefined, "error");

			if (validFiles.length === 0) return;

			const newUploads: IUploadingFile[] = validFiles.map((file) => ({
				id: `upload-${Date.now()}-${Math.random()}`,
				file,
				progress: 0,
				status: "uploading",
			}));

			setUploadingFiles((prev) => [...prev, ...newUploads]);

			let currentPhotos = [...(libraryPhotos ?? [])];

			for (const upload of newUploads) {
				const result = await simulateUpload(upload);
				if (result?.status && result.photos) {
					const newPhotos = Array.isArray(result.photos) ? result.photos : [result.photos];
					currentPhotos = [...newPhotos, ...currentPhotos];
					dispatch(setLibraryPhotos(currentPhotos));
					getTransformedSections();
					showToast("Photo uploaded successfully.", undefined, "success");
				}
			}
		},
		[activeSection, showToast, libraryPhotos, dispatch, getTransformedSections],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);

			if (activeSection?.sectionName === vendorProfileSectionName) {
				showToast("Drag & drop is disabled for Vendor Profile Images.", undefined, "error");
				return;
			}

			handleFileSelect(e.dataTransfer.files);
		},
		[handleFileSelect, activeSection],
	);

	const handlePhotoHoverStart = (photo: IPhotoLibraryPhoto) => {
		setHoveredPhotoId(photo.id);
	};

	const handlePhotoHoverEnd = () => {
		setHoveredPhotoId(null);
	};

	const togglePhotoSelection = (photo: IPhotoLibraryPhoto) => {
		const exists = selectedPhotos.some((p) => p.id === photo.id);
		let updated: IPhotoLibraryPhoto[];
		if (exists) {
			updated = selectedPhotos.filter((p) => p.id !== photo.id);
		} else {
			if (selectedPhotos.length >= MAX_SELECTABLE_PHOTOS) {
				showToast(`Only ${MAX_SELECTABLE_PHOTOS} photos can be added to this section`, undefined, "error");
				return;
			}
			updated = [...selectedPhotos, photo];
		}
		setSelectedPhotos(updated);
	};

	const triggerDeletePhotoAPI = async (photo: IPhotoLibraryPhoto) => {
		const response = await dispatch(deletePhotoFromGalleryThunk({ photoId: photo.id }));
		if (response.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			showToast(`Photo deleted successfully`, undefined, "success");
			setDeleteConfirmPhotoId(null);
			dispatch(setLibraryPhotos(libraryPhotos?.filter((p) => p.id !== photo.id) ?? []));
			getTransformedSections();
			return {
				status: true,
				message: response.payload?.message ?? "Photo deleted successfully",
			};
		} else {
			return {
				status: false,
				message: "Failed to delete photo.",
			};
		}
	};

	const initiateDelete = (photoId: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setDeleteConfirmPhotoId(photoId);
	};

	const triggerUnlinkPhotoFromSectionAPI = async (photo: IPhotoLibraryPhoto) => {
		const response = await dispatch(unlinkPhotoFromSectionThunk({ photoId: photo.id }));
		if (response.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			const unwrappedResponse = unwrapResult(response);
			dispatch(setLibraryPhotos(libraryPhotos?.filter((p) => p.id !== photo.id) ?? []));
			getTransformedSections();
			return {
				status: true,
				photo: unwrappedResponse,
			};
		} else {
			return {
				status: false,
				photo: null,
			};
		}
	};

	const initiateUnlink = (photoId: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setUnlinkConfirmPhotoId(photoId);
	};

	// Reordering within grid
	const handlePhotoGridDragOver = (targetPhotoId: string, e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (draggedPhotoId && draggedPhotoId !== targetPhotoId) {
			setDragOverPhotoId(targetPhotoId);
			setDropTargetSection(null);
		}
	};

	const handlePhotoGridDrop = (targetPhotoId: string, e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		setDraggedPhotoId(null);
		setDragOverPhotoId(null);
	};

	const confirmDelete = async (photo: IPhotoLibraryPhoto, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		const isSuccess = await triggerDeletePhotoAPI(photo);
		if (!isSuccess.status) {
			setSelectedPhotos((prev) => prev.filter((p) => p.id !== photo.id));
			setDeleteConfirmPhotoId(null);
		} else {
			showToast(isSuccess.message, undefined, "error");
		}
	};

	const cancelDelete = (e: React.MouseEvent) => {
		e.stopPropagation();
		setDeleteConfirmPhotoId(null);
	};

	const confirmUnlink = async (photo: IPhotoLibraryPhoto, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		const isSuccess = await triggerUnlinkPhotoFromSectionAPI(photo);
		if (isSuccess.status) {
			setSelectedPhotos((prev) => prev.filter((p) => p.id !== photo.id));
			setUnlinkConfirmPhotoId(null);
		}
	};

	const cancelUnlink = (e: React.MouseEvent) => {
		e.stopPropagation();
		setUnlinkConfirmPhotoId(null);
	};

	const getActiveSectionPhotos = async (sectionId: string) => {
		const sectionTypeMap: Record<string, VendorPhotoLibraryTypesEnum> = {
			[allPhotosSectionId]: VendorPhotoLibraryTypesEnum.All,
			[uncategorizedSectionId]: VendorPhotoLibraryTypesEnum.Uncategorized,
			[vendorProfileSectionId]: VendorPhotoLibraryTypesEnum.VendorProfileImages,
		};

		const type = sectionTypeMap[sectionId];
		if (type !== undefined) {
			await dispatch(getPhotoLibraryPhotosThunk({ sectionId: `${type}` }));
		} else {
			await dispatch(getPhotoLibraryPhotosThunk({ sectionId }));
		}
	};

	return (
		<>
			<div className="picker-content-wrapper">
				{/* Left Sidebar - Sections */}
				<PhotoLibrarySidebar
					isLoading={isLoading}
					sections={photoLibrarySections}
					activeSection={activeSection}
					dropTargetSection={dropTargetSection}
					onAddSection={addNewSection}
					onSectionClick={(section) => {
						dispatch(setActiveSection(section));
						getActiveSectionPhotos(section.id);
					}}
					onSectionDragOver={handleSectionDragOver}
					onSectionDragLeave={handleSectionDragLeave}
					onSectionDrop={handleSectionDrop}
					onRenameSection={renameSection}
					onDeleteSection={deleteSection}
				/>

				{/* Right Content */}
				<div className="picker-main">
					{/* Header */}
					<PhotoLibraryHeader onClose={onClose} onAddSection={addNewSection} />

					{/* Upload Section */}
					<PhotoLibraryUploadZone
						className="picker-upload-zone-mobile"
						isDragging={isDragging}
						activeSection={activeSection}
						onFileSelect={handleFileSelect}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
					/>

					<div className="picker-main-content">
						<PhotoLibraryUploadZone
							isDragging={isDragging}
							activeSection={activeSection}
							onFileSelect={handleFileSelect}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
						/>
						{uploadingFiles?.length > 0 && (
							<PhotoLibraryUploadProgress
								uploadingFiles={uploadingFiles}
								onRemoveUpload={(uploadId) => {
									setUploadingFiles((prev) => prev.filter((u) => u.id !== uploadId));
								}}
							/>
						)}

						{/* Photo Grid */}
						<PhotoLibraryGrid
							isLoading={isLoadingPhotos}
							photos={getFilteredPhotos()}
							selectedPhotos={selectedPhotos}
							hoveredPhotoId={hoveredPhotoId}
							draggedPhotoId={draggedPhotoId}
							dragOverPhotoId={dragOverPhotoId}
							deleteConfirmPhotoId={deleteConfirmPhotoId}
							unlinkConfirmPhotoId={unlinkConfirmPhotoId}
							onPhotoClick={togglePhotoSelection}
							onPhotoHoverStart={handlePhotoHoverStart}
							onPhotoHoverEnd={handlePhotoHoverEnd}
							onPhotoDragStart={handlePhotoDragStart}
							onPhotoDragEnd={handlePhotoDragEnd}
							onPhotoDragOver={handlePhotoGridDragOver}
							onPhotoDrop={handlePhotoGridDrop}
							onPreviewPhoto={triggerPreviewPhoto}
							onUnlinkPhoto={initiateUnlink}
							onDeletePhoto={initiateDelete}
							onConfirmDelete={confirmDelete}
							onCancelDelete={cancelDelete}
							onConfirmUnlink={confirmUnlink}
							onCancelUnlink={cancelUnlink}
						/>
					</div>

					{/* Footer */}
					<PhotoLibraryFooter
						selectedPhotos={selectedPhotos}
						onClose={onClose}
						onAttachmentsChange={onAttachmentsChange}
						onClearSelection={() => setSelectedPhotos([])}
					/>
				</div>
			</div>

			{/* PhotoLibraryConfirmationboxes */}
			<PhotoLibraryConfirmationboxes
				showAddSectionModal={showAddSectionModal}
				setShowAddSectionModal={setShowAddSectionModal}
				onAddSection={handleAddSection}
				sections={photoLibrarySections}
				renameSectionData={renameSectionData}
				setRenameSectionData={setRenameSectionData}
				onRenameSection={triggerRenameSectionAPI}
				activeSection={activeSection}
				setActiveSection={(section) => {
					if (section) {
						dispatch(setActiveSection(section));
					}
				}}
				deleteSectionData={deleteSectionData}
				setDeleteSectionData={setDeleteSectionData}
				onDeleteSection={handleDeleteSection}
				setFirstSectionAsActive={setFirstSectionAsActive}
			/>
		</>
	);
};

export default PhotoLibrary;
